import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AxiosError } from 'axios';
import { safeAxios } from '@/services/safeAxios';
import { log } from '@/logger/logger.ts';
import { I18N_KEYS } from '@/language/keys';
import { SERVER_ERROR_CODES } from '@/consts/server-error-codes.consts';
import { SubscriptionType } from '@/types/shared';
import { useSubscriptionPermissions } from '@/hooks/useSubscriptionPermissions/useSubscriptionPermissions';
import { getRecipeRunCostApi } from '@/api/recipe-runs/recipe-runs.api';
import useUnverifiedModelRunDialog from '@/components/Recipe/RunFlow/useUnverifiedModelRunDialog';
import { useStatusPolling } from './useToolRunStatus';
import type { RecipeRunCostRequestBody } from '@/api/recipe-runs/recipe-runs.api.types';
import type { DesignAppResult } from '@/types/design-app.types';
import type { RunEndpoints } from '@/api/toolRunEndpoints';

const logger = log.getLogger('useRunFlow');

interface UseRunFlowParams {
  endpoints: RunEndpoints;
  isGuest: boolean;
  getRunBody: (numberOfRuns: number) => RecipeRunCostRequestBody;
  onResults: (results: DesignAppResult[], userRemainingCredits?: number, workspaceRemainingCredits?: number) => void;
  /** Returning false aborts the run (sets the error message). */
  validateInputs?: () => boolean;
  /** Invoked with the new runIds right after a successful run POST (e.g. to seed latestRuns). */
  onRunStarted?: (runIds: string[]) => void;
}

// Shared run/cost/cancel/poll orchestration for the in-canvas DesignApp and the standalone
// ToolViewer; callers supply their own getRunBody and onResults.
export function useRunFlow({
  endpoints,
  isGuest,
  getRunBody,
  onResults,
  validateInputs,
  onRunStarted,
}: UseRunFlowParams) {
  const { t } = useTranslation();
  const { isSubscriptionTypeIn } = useSubscriptionPermissions();
  const { handleUnverifiedModelError } = useUnverifiedModelRunDialog();

  const [runCost, setRunCost] = useState<number | null>(null);
  const [isDynamicCost, setIsDynamicCost] = useState(false);
  const [isLoadingCost, setIsLoadingCost] = useState(true);
  const [isRunningApp, setIsRunningApp] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();

  const canCancel = useRef(false);

  const setAppAsDone = useCallback(() => {
    setIsRunningApp(false);
    canCancel.current = false;
  }, []);

  const {
    isProcessing,
    progress,
    errorMessage: pollingErrorMessage,
    prepareForRun,
    startPolling,
    numberOfLoadingResults,
  } = useStatusPolling({
    addCurrentResults: onResults,
    statusUrl: endpoints.status,
    onDonePolling: setAppAsDone,
  });

  const fetchRunCost = useCallback(
    async (numberOfRuns: number) => {
      try {
        if (isGuest) {
          return;
        }

        const costBody = getRunBody(numberOfRuns);
        const costResponse = await getRecipeRunCostApi({ costUrl: endpoints.cost, body: costBody });
        setIsDynamicCost(!!costResponse.isDynamicCost);
        setRunCost(costResponse.cost ?? 0);
      } catch (error) {
        logger.error('Failed to fetch run cost', error);
      } finally {
        setIsLoadingCost(false);
      }
    },
    [isGuest, endpoints.cost, getRunBody],
  );

  const run = useCallback(
    async (numberOfRuns: number) => {
      if (validateInputs && !validateInputs()) {
        setErrorMessage(t(I18N_KEYS.GENERAL.REQUIRED_FIELDS));
        return;
      }
      canCancel.current = false;

      const runBody = getRunBody(numberOfRuns);

      prepareForRun();
      setIsRunningApp(true);
      setErrorMessage(undefined);

      try {
        const response = await safeAxios.post(endpoints.run, runBody, {
          'axios-retry': { retries: 0 },
        });
        const runIds = response.data.runIds;
        canCancel.current = true;

        onRunStarted?.(runIds);
        startPolling(runIds);
      } catch (e: unknown) {
        logger.error('Failed to run recipe', e);

        if (e instanceof AxiosError) {
          if (e.response?.data?.internalErrorCode === SERVER_ERROR_CODES.UNVERIFIED_MODEL_NOT_APPROVED) {
            if (isSubscriptionTypeIn([SubscriptionType.Enterprise])) {
              setErrorMessage(t(I18N_KEYS.GENERAL.UNVERIFIED_MODEL_ERROR));
              setIsRunningApp(false);
              return;
            }
            await handleUnverifiedModelError(e);
            setIsRunningApp(false);
            return;
          }
        }

        setErrorMessage(e instanceof Error ? e.message : t(I18N_KEYS.GENERAL.UNKNOWN_ERROR));
        setIsRunningApp(false);
      }
    },
    [
      validateInputs,
      getRunBody,
      prepareForRun,
      endpoints.run,
      onRunStarted,
      startPolling,
      isSubscriptionTypeIn,
      handleUnverifiedModelError,
      t,
    ],
  );

  const cancelRun = useCallback(async () => {
    try {
      await safeAxios.post(endpoints.cancel);
    } catch (e) {
      logger.error('Failed to cancel prediction', e);
    }
  }, [endpoints.cancel]);

  return {
    runCost,
    isDynamicCost,
    isLoadingCost,
    isRunningApp,
    errorMessage,
    setErrorMessage,
    isProcessing,
    progress,
    pollingErrorMessage,
    numberOfLoadingResults,
    canCancel,
    startPolling,
    fetchRunCost,
    run,
    cancelRun,
  };
}

export default useRunFlow;
